import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import type {
  IncidentCloseReason,
  IncidentGroup,
  IncidentGroupActionName,
  IncidentGroupActionRequest,
  IncidentGroupActionResponse,
  IncidentReviewOutcome,
} from '../../services/incidentsApi';
import { humanizePatternGroupActionError } from '../../domain/incidents/patternGroupActionErrors';
import { incidentsApi } from '../../services/incidentsApi';
import { resolveIncidentGroupActionTarget } from '../../utils/incidentGroupKey';
import type { IncidentTimeRange } from '../../pages/incidents/useIncidentsTimeRange';
import {
  OWNER_FIELD_EMPTY_VALIDATION,
  PATTERN_OWNER_FIELD_HELPER,
} from '../../domain/incidents/groupedRepresentativeAuditCopy';
import { resolveGroupPatternActionTimeRange } from '../../domain/incidents/groupPatternActionTimeRange';
import { ownerRoleIsUnassigned } from '../../domain/incidents/incidentPatternVocabulary';
import {
  PATTERN_OWNER_LABEL,
  PATTERN_TEAM_LABEL,
  PATTERN_ACTION_SCOPE_BASELINE,
  assignOwnerScopeSummaryLine,
  assignPatternConfirmButtonLabel,
  assignPatternPendingButtonLabel,
  closePatternConfirmButtonLabel,
  closePatternPendingButtonLabel,
  patternActionConfirmationIntro,
  patternActionScopeDisplayLine,
  patternVolumeActionScopeWarningCopy,
  patternVolumeDisplayLine,
  suppressPatternConfirmButtonLabel,
  suppressPatternPendingButtonLabel,
} from '../../domain/incidents/incidentPatternVocabulary';
import {
  buildPatternActionSelectionSetModel,
  type PatternActionViewScope,
} from '../../domain/incidents/patternActionSelectionSet';
import {
  getIncidentPatternReadVolumeCount,
  patternVolumeDiffersFromActionScope,
} from '../../domain/incidents/patternReviewScopeSemantics';
import {
  BULK_PATTERN_CLOSURE_REASON_OPTIONS,
  getPatternClosureReasonExplanation,
  getPatternClosureReasonLabel,
  PATTERN_CLOSURE_QUEUE_NOTE,
  PATTERN_CLOSURE_REASON_HELPER,
  PATTERN_CLOSURE_REASON_LABEL,
  PATTERN_CLOSURE_REASON_PLACEHOLDER,
  PATTERN_CLOSURE_SELECT_BUTTON_LABEL,
} from '../../domain/incidents/patternClosureReasonCopy';
import PatternActionSelectionSet from './PatternActionSelectionSet';

export type IncidentGroupPatternActionMode = IncidentGroupActionName;

export type IncidentGroupPatternActionDialogProps = {
  open: boolean;
  mode: IncidentGroupPatternActionMode;
  group: IncidentGroup | null;
  timeRange: IncidentTimeRange;
  canManage: boolean;
  /** Prefill pattern owner hint from representative row (not follow-up/Odoo). */
  initialOwner?: string | null;
  initialAssignmentTeam?: string | null;
  /** Read-side pattern volume (episode / banner). */
  patternVolumeCount?: number;
  zoneDisplayLabel?: string;
  cameraDisplayLabel?: string;
  patternDisplayLabel?: string;
  onViewAffectedOccurrences?: (scope: PatternActionViewScope) => void;
  /** Run execute in background: close dialog immediately, notify via parent snackbar. */
  runExecuteInBackground?: boolean;
  onRunInBackground?: (payload: {
    groupKey: string;
    body: IncidentGroupActionRequest;
    mode: IncidentGroupPatternActionMode;
    matchedCount: number;
    patternLabel: string;
  }) => void;
  onClose: () => void;
  /** @deprecated prefer onRunInBackground; still invoked after sync success */
  onCompleted?: (result: IncidentGroupActionResponse) => void;
};

const REVIEW_OUTCOME_TO_CLOSE_REASON: Record<IncidentReviewOutcome, IncidentCloseReason> = {
  true_event: 'TRUE_POSITIVE',
  false_positive: 'FALSE_POSITIVE',
  demo_or_test_activity: 'TEST',
  duplicate: 'DUPLICATE',
  expected_activity: 'NOT_ACTIONABLE',
  insufficient_evidence: 'NOT_ACTIONABLE',
  not_actionable: 'NOT_ACTIONABLE',
};

const modeTitle: Record<IncidentGroupPatternActionMode, string> = {
  close: 'Close recurring risk',
  suppress: 'Suppress recurring risk',
  assign: 'Assign risk owner',
};

/** Cap membership fetch high enough for pilot windows; still bounded by server env. */
function resolveGroupActionMaxMembers(group: IncidentGroup | null | undefined): number {
  const alertCount = Math.max(0, Number(group?.alert_count || 0));
  const recent = Math.max(0, Number(group?.recent_alert_count || 0));
  const patternSource = (group as { pattern_source?: Record<string, unknown> } | null)?.pattern_source;
  const lookback = Math.max(0, Number(patternSource?.lookback_signal_count || 0));
  const selected = Math.max(0, Number(patternSource?.selected_window_alert_count || 0));
  return Math.min(
    25000,
    Math.max(2500, alertCount + 500, recent + 500, lookback + 500, selected + 500),
  );
}

// TODO: Replace free-text owner with user/team picker once Edgentik user directory and Odoo user mapping are available.
const IncidentGroupPatternActionDialog: React.FC<IncidentGroupPatternActionDialogProps> = ({
  open,
  mode,
  group,
  timeRange,
  canManage,
  initialOwner,
  initialAssignmentTeam,
  patternVolumeCount: patternVolumeCountProp = 0,
  zoneDisplayLabel,
  cameraDisplayLabel,
  patternDisplayLabel,
  onViewAffectedOccurrences,
  runExecuteInBackground = true,
  onRunInBackground,
  onClose,
  onCompleted,
}) => {
  const [preview, setPreview] = useState<IncidentGroupActionResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executeBusy, setExecuteBusy] = useState(false);
  const [executeError, setExecuteError] = useState<string | null>(null);

  const [reviewOutcome, setReviewOutcome] = useState<IncidentReviewOutcome | ''>('');
  const [closeNote, setCloseNote] = useState('');
  const [assignee, setAssignee] = useState('');
  const [assignmentTeam, setAssignmentTeam] = useState('');
  const [suppressHours, setSuppressHours] = useState('8');
  const [closeOpenMembers, setCloseOpenMembers] = useState(false);

  const effectiveTimeRange = useMemo(
    () => resolveGroupPatternActionTimeRange(group, timeRange),
    [group, timeRange],
  );
  const windowReady = Boolean(effectiveTimeRange.fromDate && effectiveTimeRange.toDate);
  const groupActionKey = String(group?.group_key || '').trim();

  /** Membership dry-run only — must not depend on owner/team typing (avoids API storm). */
  const buildMembershipPreviewRequest = useCallback((): IncidentGroupActionRequest | null => {
    if (!group) return null;
    const { groupKey, policyId } = resolveIncidentGroupActionTarget(group);
    return {
      action: mode,
      group_key: groupKey,
      from_date: effectiveTimeRange.fromDate!,
      to_date: effectiveTimeRange.toDate!,
      status_filter: 'OPEN',
      apply_to: 'open_members_in_window',
      policy_id: policyId || null,
      dry_run: true,
      max_members: resolveGroupActionMaxMembers(group),
      idempotency_key: `ui-preview-${mode}-${groupKey}-${effectiveTimeRange.fromDate}-${effectiveTimeRange.toDate}`,
    };
  }, [effectiveTimeRange.fromDate, effectiveTimeRange.toDate, group, mode]);

  const buildExecuteRequest = useCallback((): IncidentGroupActionRequest | null => {
    if (!group) return null;
    const { groupKey, policyId } = resolveIncidentGroupActionTarget(group);
    const base = {
      action: mode,
      group_key: groupKey,
      from_date: effectiveTimeRange.fromDate!,
      to_date: effectiveTimeRange.toDate!,
      status_filter: 'OPEN' as const,
      apply_to: 'open_members_in_window' as const,
      policy_id: policyId || null,
      dry_run: false,
      max_members: resolveGroupActionMaxMembers(group),
      idempotency_key: `ui-${mode}-${groupKey}-${effectiveTimeRange.fromDate}-${effectiveTimeRange.toDate}`,
    };
    if (mode === 'close') {
      if (!reviewOutcome) return null;
      return {
        ...base,
        status: 'CLOSED' as const,
        review_outcome: reviewOutcome,
        close_reason: REVIEW_OUTCOME_TO_CLOSE_REASON[reviewOutcome],
        close_note: closeNote || undefined,
      };
    }
    if (mode === 'assign') {
      return {
        ...base,
        pattern_owner: assignee.trim() || null,
        pattern_owner_team: assignmentTeam.trim() || null,
        ops_state: 'in_progress' as const,
      };
    }
    return {
      ...base,
      suppress_duration_hours: Number(suppressHours) || 8,
      suppress_reason: 'pattern_suppress',
      close_open_members: closeOpenMembers,
    };
  }, [
    assignmentTeam,
    assignee,
    closeNote,
    closeOpenMembers,
    group,
    mode,
    reviewOutcome,
    suppressHours,
    effectiveTimeRange.fromDate,
    effectiveTimeRange.toDate,
  ]);

  const loadPreview = useCallback(async () => {
    if (!open || !group || !windowReady || !canManage) return;
    const body = buildMembershipPreviewRequest();
    if (!body) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const { groupKey } = resolveIncidentGroupActionTarget(group);
      const result = await incidentsApi.applyIncidentGroupAction(groupKey, body);
      setPreview(result);
    } catch (error: any) {
      setPreview(null);
      const detail =
        error?.response?.data?.detail
        || error?.response?.data?.error
        || error?.message
        || 'Dry-run failed';
      setPreviewError(humanizePatternGroupActionError(String(detail)));
    } finally {
      setPreviewLoading(false);
    }
  }, [buildMembershipPreviewRequest, canManage, group, open, windowReady]);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setPreviewError(null);
      setExecuteError(null);
      return;
    }
    if (mode === 'assign') {
      setAssignee(String(initialOwner || '').trim());
      setAssignmentTeam(String(initialAssignmentTeam || '').trim());
    }
    if (mode === 'close') {
      setReviewOutcome('');
      setCloseNote('');
    }
    void loadPreview();
  }, [
    canManage,
    groupActionKey,
    initialAssignmentTeam,
    initialOwner,
    loadPreview,
    mode,
    open,
    effectiveTimeRange.fromDate,
    effectiveTimeRange.toDate,
  ]);

  const patternVolumeCount = Math.max(
    0,
    Number(patternVolumeCountProp || 0),
    group ? getIncidentPatternReadVolumeCount(group) : 0,
  );
  const matchedCountFromPreview =
    preview?.matched_count != null ? Math.max(0, Number(preview.matched_count)) : null;
  const scopeCountKnown = matchedCountFromPreview != null && !previewLoading;
  const matchedCount = scopeCountKnown ? matchedCountFromPreview! : 0;
  const executeAllowed = preview?.execute_allowed !== false;
  const executeBlockedReason = String(preview?.execute_blocked_reason || '').trim();
  const ownerMissing = mode === 'assign' && !assignee.trim();
  const closureReasonMissing = mode === 'close' && !reviewOutcome;
  const closureReasonExplanation = getPatternClosureReasonExplanation(reviewOutcome);
  const scopeDiffers = patternVolumeDiffersFromActionScope(
    patternVolumeCount,
    matchedCountFromPreview,
  );
  const scopeWarning =
    scopeDiffers && scopeCountKnown
      ? patternVolumeActionScopeWarningCopy(patternVolumeCount, matchedCount)
      : null;

  const selectionSetModel = useMemo(
    () => buildPatternActionSelectionSetModel({
      group,
      timeRange: { ...timeRange, ...effectiveTimeRange },
      matchedCount: matchedCountFromPreview,
      previewLoading,
      patternLabel: patternDisplayLabel,
      zoneLabel: zoneDisplayLabel,
      cameraLabel: cameraDisplayLabel,
    }),
    [
      cameraDisplayLabel,
      effectiveTimeRange,
      group,
      matchedCountFromPreview,
      patternDisplayLabel,
      previewLoading,
      timeRange,
      zoneDisplayLabel,
    ],
  );

  const confirmLabel = useMemo(() => {
    if (mode === 'close' && closureReasonMissing) {
      return PATTERN_CLOSURE_SELECT_BUTTON_LABEL;
    }
    if (!scopeCountKnown) {
      if (mode === 'close') return closePatternPendingButtonLabel();
      if (mode === 'suppress') return suppressPatternPendingButtonLabel();
      return assignPatternPendingButtonLabel();
    }
    if (mode === 'close') return closePatternConfirmButtonLabel(matchedCount);
    if (mode === 'suppress') return suppressPatternConfirmButtonLabel(matchedCount, closeOpenMembers);
    return assignPatternConfirmButtonLabel(matchedCount);
  }, [closeOpenMembers, closureReasonMissing, matchedCount, mode, scopeCountKnown]);

  const confirmationIntro = useMemo(() => {
    if (!scopeCountKnown) return '';
    return patternActionConfirmationIntro(mode, matchedCount, patternVolumeCount);
  }, [matchedCount, mode, patternVolumeCount, scopeCountKnown]);

  const patternLabel = String(
    patternDisplayLabel
    || group?.incident_type
    || (group as any)?.summary_text
    || 'recurring risk',
  ).trim();

  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown' | undefined,
  ) => {
    // MUI Select menus render in a portal; picking an option often hits the dialog backdrop.
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const handleExecute = async () => {
    if (!group || !canManage) return;
    const body = buildExecuteRequest();
    if (!body) return;
    const { groupKey } = resolveIncidentGroupActionTarget(group);

    if (runExecuteInBackground && onRunInBackground) {
      onRunInBackground({
        groupKey,
        body,
        mode,
        matchedCount,
        patternLabel,
      });
      onClose();
      return;
    }

    setExecuteBusy(true);
    setExecuteError(null);
    try {
      const result = await incidentsApi.applyIncidentGroupAction(groupKey, body);
      onCompleted?.(result);
      onClose();
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail
        || error?.response?.data?.error
        || error?.message
        || 'Action failed';
      setExecuteError(humanizePatternGroupActionError(String(detail)));
    } finally {
      setExecuteBusy(false);
    }
  };

  const dialogTitle = mode === 'assign' && !ownerRoleIsUnassigned(initialOwner)
    ? `Update ${PATTERN_OWNER_LABEL.toLowerCase()}`
    : modeTitle[mode];

  const assignWindowLabel = [
    String(timeRange.label || '').trim(),
    String(zoneDisplayLabel || '').trim(),
  ].filter(Boolean).join(' · ');

  const blockedReasonDisplay = executeBlockedReason
    ? humanizePatternGroupActionError(executeBlockedReason)
    : `Too many open observations match this action (${matchedCount.toLocaleString()}). Narrow the review window and try again.`;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {!windowReady && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Set a valid time range (from/to) before applying a recurring-risk action.
          </Alert>
        )}

        {mode === 'assign' ? (
          <>
            {previewLoading ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                data-testid="assign-owner-scope-summary"
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Counting open observations…
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, lineHeight: 1.55 }}
                data-testid="assign-owner-scope-summary"
              >
                {assignOwnerScopeSummaryLine(
                  scopeCountKnown ? matchedCount : null,
                  { pending: !scopeCountKnown, windowLabel: assignWindowLabel },
                )}
              </Typography>
            )}
            {!previewLoading && preview && !executeAllowed ? (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="pattern-action-blocked">
                {blockedReasonDisplay}
              </Alert>
            ) : null}
            {previewError ? <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert> : null}
            {executeError ? <Alert severity="error" sx={{ mb: 2 }}>{executeError}</Alert> : null}
            <TextField
              fullWidth
              required
              label={PATTERN_OWNER_LABEL}
              placeholder="e.g. safety-lead@site.com"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              error={ownerMissing}
              helperText={ownerMissing ? OWNER_FIELD_EMPTY_VALIDATION : PATTERN_OWNER_FIELD_HELPER}
              sx={{ mb: 2 }}
              data-testid="assign-risk-owner-field"
            />
            <TextField
              fullWidth
              label={`${PATTERN_TEAM_LABEL} (optional)`}
              placeholder="e.g. Safety, Warehouse Ops"
              value={assignmentTeam}
              onChange={(e) => setAssignmentTeam(e.target.value)}
            />
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35, lineHeight: 1.55 }}>
              {patternVolumeDisplayLine(patternVolumeCount)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.55 }}>
              {PATTERN_ACTION_SCOPE_BASELINE}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }} data-testid="pattern-action-scope-alert">
              {previewLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Resolving action scope (open members in selected window)…
                </Box>
              ) : scopeCountKnown ? (
                <>
                  <Typography variant="body2" component="span" sx={{ fontWeight: 700 }}>
                    {patternActionScopeDisplayLine(matchedCount)}
                  </Typography>
                  {confirmationIntro ? (
                    <Typography variant="body2" component="div" sx={{ mt: 1, lineHeight: 1.55 }}>
                      {confirmationIntro}
                    </Typography>
                  ) : null}
                </>
              ) : (
                <>
                  {patternActionScopeDisplayLine(null, { pending: true })}
                  <Button size="small" variant="text" onClick={() => void loadPreview()} sx={{ ml: 0.5, px: 0 }}>
                    Refresh count
                  </Button>
                </>
              )}
            </Alert>
            {scopeWarning ? (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="pattern-scope-differ-warning">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {scopeWarning.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.55 }}>
                  {scopeWarning.body}
                  <br />
                  <br />
                  {scopeWarning.footer}
                </Typography>
              </Alert>
            ) : null}
            <PatternActionSelectionSet model={selectionSetModel} />
            {onViewAffectedOccurrences && selectionSetModel.viewScope ? (
              <Button
                size="small"
                variant="text"
                sx={{ mb: 2, px: 0, fontWeight: 700 }}
                data-testid="view-affected-occurrences"
                onClick={() => {
                  onViewAffectedOccurrences(selectionSetModel.viewScope!);
                  onClose();
                }}
              >
                View affected occurrences
              </Button>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 2, lineHeight: 1.5 }}
                data-testid="view-affected-occurrences-fallback"
              >
                View affected occurrences — set a valid time window and group key first.
              </Typography>
            )}
            {!previewLoading && preview && !executeAllowed ? (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="pattern-action-blocked">
                {blockedReasonDisplay}
              </Alert>
            ) : null}
            {previewError ? <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert> : null}
            {executeError ? <Alert severity="error" sx={{ mb: 2 }}>{executeError}</Alert> : null}
            {runExecuteInBackground && onRunInBackground ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.55 }}>
                Choosing a closure reason does not apply yet. Click {confirmLabel} when ready — the dialog will
                close and a notification will show progress, then completion.
              </Typography>
            ) : null}

            {mode === 'close' ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
                  This closes matching open linked records in the selected review window, not just the representative evidence sample.
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }} data-testid="pattern-closure-reason-field">
                  <InputLabel id="pattern-closure-reason-label">{PATTERN_CLOSURE_REASON_LABEL}</InputLabel>
                  <Select
                    labelId="pattern-closure-reason-label"
                    label={PATTERN_CLOSURE_REASON_LABEL}
                    value={reviewOutcome}
                    displayEmpty
                    renderValue={(selected) => getPatternClosureReasonLabel(selected as IncidentReviewOutcome | '')}
                    onChange={(e) => {
                      const value = String(e.target.value || '').trim();
                      setReviewOutcome(value ? (value as IncidentReviewOutcome) : '');
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{PATTERN_CLOSURE_REASON_PLACEHOLDER}</em>
                    </MenuItem>
                    {BULK_PATTERN_CLOSURE_REASON_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ mx: 0, lineHeight: 1.55 }}>
                    {PATTERN_CLOSURE_REASON_HELPER}
                  </FormHelperText>
                </FormControl>
                {closureReasonExplanation ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="pattern-closure-reason-explanation"
                    sx={{ mb: 1.5, lineHeight: 1.55 }}
                  >
                    {closureReasonExplanation}
                  </Typography>
                ) : null}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="pattern-closure-queue-note"
                  sx={{ display: 'block', mb: 1.5, lineHeight: 1.55 }}
                >
                  {PATTERN_CLOSURE_QUEUE_NOTE}
                </Typography>
                <TextField
                  fullWidth
                  label="Note (optional)"
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  multiline
                  minRows={2}
                />
              </>
            ) : null}

            {mode === 'suppress' ? (
              <>
                <TextField
                  fullWidth
                  label="Suppress duration (hours)"
                  value={suppressHours}
                  onChange={(e) => setSuppressHours(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Also close open members?</InputLabel>
                  <Select
                    label="Also close open members?"
                    value={closeOpenMembers ? 'yes' : 'no'}
                    onChange={(e) => setCloseOpenMembers(e.target.value === 'yes')}
                  >
                    <MenuItem value="no">Suppress only (keep open rows)</MenuItem>
                    <MenuItem value="yes">Suppress and close as expected activity</MenuItem>
                  </Select>
                </FormControl>
              </>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={executeBusy}>
          Cancel
        </Button>
        {mode !== 'assign' ? (
          <Button onClick={() => void loadPreview()} disabled={previewLoading || executeBusy}>
            Refresh count
          </Button>
        ) : null}
        <Button
          variant="contained"
          color={mode === 'close' ? 'success' : 'primary'}
          disabled={
            !canManage
            || !windowReady
            || previewLoading
            || executeBusy
            || !scopeCountKnown
            || matchedCount <= 0
            || !executeAllowed
            || ownerMissing
            || closureReasonMissing
          }
          onClick={() => void handleExecute()}
        >
          {executeBusy ? 'Applying…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentGroupPatternActionDialog;
